// src/components/dashboard/ActivityFeed.js

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboard } from '../../contexts/DashboardContext';
import '../../styles/dashboard-widgets.css';

const ActivityFeed = () => {
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  
  const { 
    filteredRecentActivity,
    activityLoading,
    activityError,
    departmentFilter,
    setDepartmentFilter,
    searchFilter,
    setSearchFilter,
    loadRecentActivity,
    activityHasMore,
    formatTime,
    departments,
    departmentsLoading
  } = useDashboard();

  const [displayCount, setDisplayCount] = useState(10);
  const [activityTypeFilter, setActivityTypeFilter] = useState('all');

  // Filter activities by type
  const filteredActivities = useMemo(() => {
    let filtered = filteredRecentActivity;

    if (activityTypeFilter !== 'all') {
      filtered = filtered.filter(activity => activity.type === activityTypeFilter);
    }

    return filtered.slice(0, displayCount);
  }, [filteredRecentActivity, activityTypeFilter, displayCount]);

  // Get activity type counts for filter buttons
  const activityTypeCounts = useMemo(() => {
    const counts = {
      all: filteredRecentActivity.length,
      'clock-in': 0,
      'clock-out': 0,
      'break-start': 0,
      'break-end': 0
    };

    filteredRecentActivity.forEach(activity => {
      if (counts[activity.type] !== undefined) {
        counts[activity.type]++;
      }
    });

    return counts;
  }, [filteredRecentActivity]);

  const getActivityBaseDescription = (activity) => {
    const baseDesc = {
      'clock-in': t('activityFeed.activityDescriptions.clockedIn'),
      'clock-out': t('activityFeed.activityDescriptions.clockedOut'),
      'break-start': t('activityFeed.activityDescriptions.startedBreak'),
      'break-end': t('activityFeed.activityDescriptions.endedBreak')
    };

    return baseDesc[activity.type] || 'Activity';
  };

  const getWorkedDurationText = (activity) => {
    if (activity.type === 'clock-out' && activity.totalWorkDuration) {
      // Convert milliseconds to total minutes
      const totalMinutes = Math.floor(activity.totalWorkDuration / 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (hours > 0) {
        return `${hours}h ${minutes}m ${t('activityFeed.activityDescriptions.worked')}`;
      }
      return `${minutes}m ${t('activityFeed.activityDescriptions.worked')}`;
    }
    return null;
  };

  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const diff = Math.floor((now - timestamp) / 1000); // in seconds
    const isGerman = i18n.language === 'de';

    if (diff < 60) {
      return t('activityFeed.timeRelative.justNow');
    } else if (diff < 3600) {
      const minutes = Math.floor(diff / 60);
      if (isGerman) {
        return `vor ${minutes} Min`;
      }
      return `${minutes}${t('activityFeed.timeRelative.minutesAgo')}`;
    } else if (diff < 86400) {
      const hours = Math.floor(diff / 3600);
      if (isGerman) {
        return `vor ${hours} Std`;
      }
      return `${hours}${t('activityFeed.timeRelative.hoursAgo')}`;
    } else if (diff < 604800) {
      const days = Math.floor(diff / 86400);
      if (isGerman) {
        return `vor ${days} T`;
      }
      return `${days}${t('activityFeed.timeRelative.daysAgo')}`;
    } else {
      return timestamp.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
      });
    }
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 10);
  };

  const handleActivityTypeFilter = (type) => {
    setActivityTypeFilter(type);
    setDisplayCount(10); // Reset display count when changing filter
  };

  const clearAllFilters = () => {
    setDepartmentFilter('all');
    setSearchFilter('');
    setActivityTypeFilter('all');
    setDisplayCount(10);
  };

  // BUGFIX (Recent Activity tab stuck empty until hard refresh): gives the
  // admin a manual, immediate way to retry the fetch (in addition to
  // DashboardContext's automatic activeTab-triggered retry effect) whenever
  // a genuine error is surfaced, instead of requiring a full page reload.
  const handleRetry = () => {
    loadRecentActivity(true);
  };

  if (activityLoading && filteredActivities.length === 0) {
    return (
      <div className="activity-feed-widget">
        <div className="widget-header">
          <h3>{t('recentActivity')}</h3>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('activityFeed.loadingActivity')}</p>
        </div>
      </div>
    );
  }

  if (activityError) {
    return (
      <div className="activity-feed-widget">
        <div className="widget-header">
          <h3>{t('recentActivity')}</h3>
        </div>
        <div className="error-state">
          <p className="error-message">× {activityError}</p>
          <button
            onClick={handleRetry}
            className="clear-filters-btn"
            disabled={activityLoading}
          >
            {activityLoading ? t('common:common.loading') : t('activityFeed.actions.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-feed-widget">
      <div className="widget-header">
        <h3>{t('recentActivity')}</h3>
        <div className="stats-summary">
          <span className="stat">
            <strong>{activityTypeCounts.all}</strong> {t('activityFeed.totalActivities')}
          </span>
        </div>
      </div>

      <div className="widget-filters">
        <div className="filter-group">
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="department-filter"
            disabled={departmentsLoading}
          >
            <option value="all">
              {departmentsLoading ? t('activityFeed.loadingDepartments') : t('allDepartments')}
            </option>
            {departments.map(dept => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>

          <select
            value={activityTypeFilter}
            onChange={(e) => handleActivityTypeFilter(e.target.value)}
            className="activity-type-filter"
          >
            <option value="all">{t('activityFeed.allActivities')} ({activityTypeCounts.all})</option>
            <option value="clock-in">{t('activityFeed.clockIn')} ({activityTypeCounts['clock-in']})</option>
            <option value="clock-out">{t('activityFeed.clockOut')} ({activityTypeCounts['clock-out']})</option>
            <option value="break-start">{t('activityFeed.breakStart')} ({activityTypeCounts['break-start']})</option>
            <option value="break-end">{t('activityFeed.breakEnd')} ({activityTypeCounts['break-end']})</option>
          </select>

          <input
            type="text"
            placeholder={t('searchByName')}
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="search-filter"
          />
        </div>
      </div>

      {filteredActivities.length === 0 ? (
        <div className="empty-state">
          {filteredRecentActivity.length === 0 && !searchFilter && departmentFilter === 'all' && activityTypeFilter === 'all' ? (
            <>
              <div className="empty-icon">○</div>
              <h4>{t('activityFeed.emptyStates.noRecentActivity')}</h4>
              <p>{t('activityFeed.emptyStates.noActivityMessage')}</p>
            </>
          ) : (
            <>
              <div className="empty-icon">∅</div>
              <h4>{t('activityFeed.emptyStates.noActivitiesFound')}</h4>
              <p>{t('activityFeed.emptyStates.noMatchingFilters')}</p>
              <button 
                onClick={clearAllFilters}
                className="clear-filters-btn"
              >
                {t('activityFeed.actions.clearAllFilters')}
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="activity-list">
            {filteredActivities.map((activity) => {
              const workedDurationText = getWorkedDurationText(activity);

              return (
                <div key={activity.id} className={`activity-item ${activity.type}`}>                
                  <div className="activity-content">
                    {/* Row 1 — staff name (left) and absolute timestamp (right),
                        aligned on the same horizontal line via space-between. */}
                    <div className="activity-header">
                      <div className="staff-info">
                        <span className="staff-name">{activity.staffName}</span>
                      </div>
                      <span className="timestamp">{formatTime(activity.timestamp)}</span>
                    </div>
                    
                    {/* Row 2 — description (left) and relative time (right),
                        aligned on the same horizontal line via space-between. */}
                    <div className="activity-details">
                      <div className="activity-description">
                        {getActivityBaseDescription(activity)}
                        {workedDurationText && (
                          <>
                            <span className="description-separator"> · </span>
                            {workedDurationText}
                          </>
                        )}
                        <span className="location-device-separator"> · </span>
                        {activity.departmentName}
                        {activity.deviceType && activity.deviceType !== 'Unknown' && (
                          <>
                            <span className="location-device-separator"> · </span>
                            <span className="device-type">{activity.deviceType}</span>
                          </>
                        )}
                      </div>
                      <span className="relative-time">{getRelativeTime(activity.timestamp)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Load More / Pagination */}
          {(displayCount < filteredRecentActivity.length || activityHasMore) && (
            <div className="widget-footer">
              <div className="load-more-section">
                {displayCount < filteredRecentActivity.length && (
                  <button 
                    onClick={handleLoadMore}
                    className="load-more-btn"
                  >
                    {t('activityFeed.actions.showMoreActivities')}
                  </button>
                )}
                
                {displayCount >= filteredRecentActivity.length && activityHasMore && (
                  <button 
                    onClick={() => loadRecentActivity(false)}
                    className="load-more-btn"
                    disabled={activityLoading}
                  >
                    {activityLoading ? t('common:common.loading') : t('activityFeed.actions.loadMoreFromDatabase')}
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Footer with summary */}
      {filteredActivities.length > 0 && (
        <div className="widget-footer">
          <p className="summary-text">
            {t('activityFeed.summary.showing')} {filteredActivities.length} {t('activityFeed.summary.of')} {filteredRecentActivity.length} {t('activityFeed.summary.activities')}
            {(departmentFilter !== 'all' || searchFilter || activityTypeFilter !== 'all') && (
              <button 
                onClick={clearAllFilters}
                className="clear-filters-link"
              >
                {t('activityFeed.actions.clearFilters')}
              </button>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;